import { Skeleton, toast } from "@credit-acceptance/uikit";
import { CancelledError, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useEffect, useId, useRef, useState } from "react";
import { type Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import ConfirmDialog from "../../../../../components/ui/confirmDialog";
import { DialogFooter } from "../../../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Checkbox } from "../../../../../components/ui/uikit/checkbox";
import { Textarea } from "../../../../../components/ui/uikit/textarea";
import { formatSsn } from "../../../../../lib/format";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { cn } from "../../../../../lib/utils";
import ForceExitWarningModal from "../../../../exit/ForceExitWarningModal";
import { useDscWorkbench } from "../../../api/useDscWorkbench";
import {
  mapAccessories,
  mapFinancialToSavedDealFields,
  mapResidenceToSavedFields,
  rankPhones,
  selectLivePhones,
} from "../api/callScriptResidence.mapping";
import { buildResidenceVerificationAnswers } from "../api/callScriptResidence.savePayload";
import {
  EMPTY_RESIDENCE_VERIFIED_FIELDS,
  type ResidenceFieldKey,
  type ResidenceQuestionFieldKey,
  type ResidenceVerifiedFields,
} from "../api/callScriptResidence.types";
import {
  buildResidenceQuestionPkMap,
  resolveResidenceVerificationQuestionType,
} from "../api/callScriptResidenceQuestions";
import { type ResidenceFieldErrors, validateResidenceCallScript } from "../api/callScriptResidenceValidation";
import { useCallScriptResidenceHistory } from "../api/useCallScriptResidenceHistory";
import {
  callScriptResidenceQuestionsQuery,
  ensureCallScriptResidenceQuestionsForPerson,
  prefetchAllCallScriptResidenceQuestions,
} from "../api/useCallScriptResidenceQuestions";
import { useSaveCallScriptResidence } from "../api/useSaveCallScriptResidence";
import { RESIDENCE_OWNER_TYPE_DEFAULT, RESIDENCE_OWNER_TYPE_OPTIONS, US_STATE_OPTIONS } from "../constants";
import { AddressVerificationPanel } from "../fields/AddressVerificationPanel";
import { formatPhoneOrDigits } from "../fields/PhoneNumberInput";
import { ZipLookupMessage } from "../fields/ZipLookupMessage";
import { useFormReadOnly } from "../readOnlyContext";
import type { SignersInput } from "../Signers.schema";
import {
  buildChangedFieldRows,
  type ChangedFieldRow,
  type ComparisonRowOfKinds,
  isRowChanged,
} from "./callScriptComparison";
import { CallScriptConfirmSaveContent } from "./CallScriptConfirmSaveDialog";
import { CallScriptDialogShell } from "./CallScriptDialogShell";
import { CallScriptFieldControl } from "./CallScriptFieldControl";
import { CallScriptHistoryPanel } from "./CallScriptHistoryPanel";
import { CallScriptSaveBanners } from "./CallScriptSaveBanners";
import { useCallScriptAddressStandardization } from "./useCallScriptAddressStandardization";
import { useCallScriptOpenOrchestration } from "./useCallScriptOpenOrchestration";
import { useCallScriptZipAutofill, type ZipAutofillResult } from "./useCallScriptZipAutofill";

type PersonPath = "driver" | "secondSigner";

/**
 * The modal's panels. The address-compare panel (with its ignore warning)
 * shows on top of these while standardization awaits the analyst — see
 * useCallScriptAddressStandardization.
 */
type Step = "verification" | "confirm";

export type { ResidenceCallScriptAccessory, ResidenceVerifiedFields } from "../api/callScriptResidence.types";
export { EMPTY_RESIDENCE_VERIFIED_FIELDS } from "../api/callScriptResidence.types";

/** Any single field's value, for the generic row/control helpers below. */
type ResidenceFieldValue = ResidenceVerifiedFields[ResidenceFieldKey];

// ---------------------------------------------------------------------------
// Row definitions
// ---------------------------------------------------------------------------

/** The control kinds this script uses — employment's `checkbox`/`number` have no residence rows. */
type ResidenceRowKind = "text" | "phone" | "currency" | "date" | "yesNo" | "yesNoBlank" | "select";

type FieldRow = ComparisonRowOfKinds<ResidenceFieldKey, ResidenceRowKind> & {
  type: "field";
  /**
   * The row's verification question. The row renders only while that
   * question is in the deal's catalog — recap filters it to the deal's
   * submission-date vintage, which is CAPS2's question-expiry rule. Absent
   * for the five down-payment amounts, which always render.
   */
  flag?: ResidenceQuestionFieldKey;
  /** Hidden while the residence is owned by Self or Spouse. */
  landlord?: true;
};

/** A full-width section heading (always rendered — CAPS2 puts no flag on either address header). */
interface SectionRow {
  type: "section";
  key: string;
  label: string;
  /** Renders the "same as physical" checkbox beside the heading (Mailing Address only). */
  withSameAsPhysical?: boolean;
  /** Hidden while the residence is owned by Self or Spouse. */
  landlord?: true;
}

type Row = FieldRow | SectionRow;

// City is free text filled by the ZIP lookup: the lookup returns a single
// city, so CAPS2's city-list dropdown has nothing to list.
function buildRows(t: TFunction): Row[] {
  return [
    // --- Contact block ---
    {
      type: "field",
      key: "phoneNumberDialed",
      flag: "phoneNumberDialed",
      label: t("review.customer.callScriptModal.fields.phoneNumberDialed"),
      kind: "phone",
      placeholder: "000-000-0000",
    },
    {
      type: "field",
      key: "wasOriginalNumberBad",
      flag: "wasOriginalNumberBad",
      label: t("review.customer.callScriptModal.fields.wasOriginalNumberBad"),
      kind: "yesNo",
    },
    {
      type: "field",
      key: "dateOfBirth",
      flag: "dateOfBirth",
      label: t("review.customer.callScriptModal.fields.dateOfBirth"),
      kind: "date",
    },
    {
      type: "field",
      key: "primaryNumber",
      flag: "primaryNumber",
      label: t("review.customer.callScriptModal.fields.primaryNumber"),
      kind: "phone",
      placeholder: "000-000-0000",
    },
    {
      type: "field",
      key: "secondaryNumber",
      flag: "secondaryNumber",
      label: t("review.customer.callScriptModal.fields.secondaryNumber"),
      kind: "phone",
      placeholder: "000-000-0000",
    },
    {
      type: "field",
      key: "signedCopyProvided",
      flag: "signedCopyProvided",
      label: t("review.customer.callScriptModal.fields.signedCopyProvided"),
      kind: "yesNoBlank",
    },

    // --- Physical address ---
    { type: "section", key: "physicalAddress", label: t("review.customer.callScriptModal.sections.physicalAddress") },
    {
      type: "field",
      key: "physicalStreetNumber",
      flag: "physicalStreetNumber",
      label: t("review.customer.callScriptModal.fields.physicalStreetNumber"),
      kind: "text",
    },
    {
      type: "field",
      key: "physicalStreetName",
      flag: "physicalStreetName",
      label: t("review.customer.callScriptModal.fields.physicalStreetName"),
      kind: "text",
    },
    {
      type: "field",
      key: "physicalApartmentNumber",
      flag: "physicalApartmentNumber",
      label: t("review.customer.callScriptModal.fields.physicalApartmentNumber"),
      kind: "text",
    },
    {
      type: "field",
      key: "physicalZip",
      flag: "physicalZip",
      label: t("review.customer.callScriptModal.fields.physicalZip"),
      kind: "text",
      placeholder: "00000",
    },
    {
      type: "field",
      key: "physicalCity",
      flag: "physicalCity",
      label: t("review.customer.callScriptModal.fields.physicalCity"),
      kind: "text",
    },
    {
      type: "field",
      key: "physicalState",
      flag: "physicalState",
      label: t("review.customer.callScriptModal.fields.physicalState"),
      kind: "select",
      options: US_STATE_OPTIONS,
    },

    // --- Mailing address ---
    {
      type: "section",
      key: "mailingAddress",
      label: t("review.customer.callScriptModal.sections.mailingAddress"),
      withSameAsPhysical: true,
    },
    {
      type: "field",
      key: "mailingStreetNumber",
      flag: "mailingStreetNumber",
      label: t("review.customer.callScriptModal.fields.mailingStreetNumber"),
      kind: "text",
    },
    {
      type: "field",
      key: "mailingStreetName",
      flag: "mailingStreetName",
      label: t("review.customer.callScriptModal.fields.mailingStreetName"),
      kind: "text",
    },
    {
      type: "field",
      key: "mailingApartmentNumber",
      flag: "mailingApartmentNumber",
      label: t("review.customer.callScriptModal.fields.mailingApartmentNumber"),
      kind: "text",
    },
    {
      type: "field",
      key: "mailingZip",
      flag: "mailingZip",
      label: t("review.customer.callScriptModal.fields.mailingZip"),
      kind: "text",
      placeholder: "00000",
    },
    // NOTE: CAPS2 gates this row's SAVED cell on verificationMAStateRendered
    // instead of verificationMACityRendered (known defect #1 — the saved
    // Mailing City disappears whenever Mailing State expires, leaving the
    // input with nothing to compare against). Not reproduced here: the whole
    // row, both columns, hangs off the City flag like the Physical block does.
    {
      type: "field",
      key: "mailingCity",
      flag: "mailingCity",
      label: t("review.customer.callScriptModal.fields.mailingCity"),
      kind: "text",
    },
    {
      type: "field",
      key: "mailingState",
      flag: "mailingState",
      label: t("review.customer.callScriptModal.fields.mailingState"),
      kind: "select",
      options: US_STATE_OPTIONS,
    },

    // --- Residence type + landlord (note-only: no verification question) ---
    {
      type: "field",
      key: "residenceOwnerType",
      label: t("review.customer.callScriptModal.fields.residenceOwnerType"),
      kind: "select",
      options: RESIDENCE_OWNER_TYPE_OPTIONS,
    },
    {
      type: "section",
      key: "landlordInformation",
      label: t("review.customer.callScriptModal.sections.landlordInformation"),
      landlord: true,
    },
    {
      type: "field",
      key: "landlordFirstName",
      label: t("review.customer.callScriptModal.fields.landlordFirstName"),
      kind: "text",
      landlord: true,
    },
    {
      type: "field",
      key: "landlordLastName",
      label: t("review.customer.callScriptModal.fields.landlordLastName"),
      kind: "text",
      landlord: true,
    },
    {
      type: "field",
      key: "landlordCompanyName",
      label: t("review.customer.callScriptModal.fields.landlordCompanyName"),
      kind: "text",
      landlord: true,
    },
    {
      type: "field",
      key: "landlordPhone",
      label: t("review.customer.callScriptModal.fields.landlordPhone"),
      kind: "phone",
      placeholder: "000-000-0000",
      landlord: true,
    },
    {
      type: "field",
      key: "landlordStreetNumber",
      label: t("review.customer.callScriptModal.fields.landlordStreetNumber"),
      kind: "text",
      landlord: true,
    },
    {
      type: "field",
      key: "landlordStreetName",
      label: t("review.customer.callScriptModal.fields.landlordStreetName"),
      kind: "text",
      landlord: true,
    },
    {
      type: "field",
      key: "landlordApartmentNumber",
      label: t("review.customer.callScriptModal.fields.landlordApartmentNumber"),
      kind: "text",
      landlord: true,
    },
    {
      type: "field",
      key: "landlordZip",
      label: t("review.customer.callScriptModal.fields.landlordZip"),
      kind: "text",
      placeholder: "00000",
      landlord: true,
    },
    {
      type: "field",
      key: "landlordCity",
      label: t("review.customer.callScriptModal.fields.landlordCity"),
      kind: "text",
      landlord: true,
    },
    {
      type: "field",
      key: "landlordState",
      label: t("review.customer.callScriptModal.fields.landlordState"),
      kind: "select",
      options: US_STATE_OPTIONS,
      landlord: true,
    },

    // --- Deal / down payment block ---
    {
      type: "field",
      key: "whoIsPrimaryDriver",
      flag: "whoIsPrimaryDriver",
      label: t("review.customer.callScriptModal.fields.whoIsPrimaryDriver"),
      kind: "text",
    },
    {
      type: "field",
      key: "tradeIn",
      flag: "tradeIn",
      label: t("review.customer.callScriptModal.fields.tradeIn"),
      kind: "yesNoBlank",
    },
    // The five below have no verification question in CAPS2 — always shown,
    // populated by buildScreenForDeferredDown(dealsInWork, deferredDownPaymentDetail).
    {
      type: "field",
      key: "grossTradeInAmount",
      label: t("review.customer.callScriptModal.fields.grossTradeInAmount"),
      kind: "currency",
    },
    {
      type: "field",
      key: "tradeInPayoffAmount",
      label: t("review.customer.callScriptModal.fields.tradeInPayoffAmount"),
      kind: "currency",
    },
    {
      type: "field",
      key: "cashDownPaidOnDelivery",
      label: t("review.customer.callScriptModal.fields.cashDownPaidOnDelivery"),
      kind: "currency",
    },
    {
      type: "field",
      key: "amountDueAfterDelivery",
      label: t("review.customer.callScriptModal.fields.amountDueAfterDelivery"),
      kind: "currency",
    },
    {
      type: "field",
      key: "whenIsFinalAmountDue",
      label: t("review.customer.callScriptModal.fields.whenIsFinalAmountDue"),
      kind: "date",
    },
  ];
}

/** The six mailing fields, paired with the physical field each mirrors while "same as physical" is checked. */
const MAILING_MIRROR_PAIRS: ReadonlyArray<[keyof ResidenceVerifiedFields, keyof ResidenceVerifiedFields]> = [
  ["mailingStreetNumber", "physicalStreetNumber"],
  ["mailingStreetName", "physicalStreetName"],
  ["mailingApartmentNumber", "physicalApartmentNumber"],
  ["mailingZip", "physicalZip"],
  ["mailingCity", "physicalCity"],
  ["mailingState", "physicalState"],
];

const MAILING_FIELD_KEYS = new Set<ResidenceFieldKey>(MAILING_MIRROR_PAIRS.map(([mailingKey]) => mailingKey));

/** Free-text address inputs CAPS2 upper-cases on blur. */
const UPPERCASE_ON_BLUR_KEYS = new Set<ResidenceFieldKey>([
  "physicalStreetNumber",
  "physicalStreetName",
  "physicalApartmentNumber",
  "physicalCity",
  "mailingStreetNumber",
  "mailingStreetName",
  "mailingApartmentNumber",
  "mailingCity",
  "landlordFirstName",
  "landlordLastName",
  "landlordCompanyName",
  "landlordStreetNumber",
  "landlordStreetName",
  "landlordApartmentNumber",
  "landlordCity",
]);

/** CAPS2's Verification Notes limit. */
const NOTE_MAX_LENGTH = 2000;

interface CallScriptModalResidenceProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Deal / approval identifier — path param for both Call Script calls, and displayed in the side panel. */
  approvalId?: string;
  /** Applicant identifier — path param for both Call Script calls. */
  applicantId?: string;
  /** Which signer this call is for — drives the title and the side panel's name/SSN. */
  personPath: PersonPath;
  /** RHF control — reads the person's name/SSN/primary phone for the side panel. */
  control: Control<SignersInput>;
}

// ---------------------------------------------------------------------------
// CallScriptModalResidence
//
// Draggable, dismissable "Customer Verification" call script — the residence
// sibling of CallScriptModal.tsx (employment). In CAPS2 these are two entirely
// separate screens with no shared code: this one is
// step6_customer_verification.xhtml, driven by
// Step6ApplicantResidenceVerificationBackingBean, opened from the driver /
// second-signer phone+email panel's "Call Script" button.
//
// Save vs. close, deliberately split (a verification session can involve
// several saves before the analyst is done): a successful save refreshes this
// applicant's history and clears "Verified Information" back to blank for the
// next call, while closing the modal has nothing special to do as a result.
//
// SCOPE: Save POSTs .../residence-verification/call-script
// (useSaveCallScriptResidence) and the History panel reads
// .../residence-verification/call-script/history
// (useCallScriptResidenceHistory). The verification-question catalog
// (useCallScriptResidenceQuestions) is prefetched on open and awaited at Save
// to resolve each answer's verificationQuestionPk. "Saved Information" and the
// Vehicle Accessories table are read live from the dashboard, same as the
// employment modal — see callScriptResidence.mapping. The residence-type and
// landlord rows have no question, so they reach recap only via the note text.
// A complete Physical, Mailing or Landlord ZIP fills that block's City/State
// (useCallScriptZipAutofill).
// ---------------------------------------------------------------------------
export function CallScriptModalResidence({
  open,
  onOpenChange,
  approvalId,
  applicantId,
  personPath,
  control,
}: CallScriptModalResidenceProperties) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const readOnly = useFormReadOnly();
  const uid = useId();
  const hasIds = Boolean(approvalId && applicantId);

  const dashboard = useDscWorkbench(approvalId ?? "");
  const residence = dashboard.data?.[personPath]?.residence;
  const financial = dashboard.data?.financial;
  // CAPS2 keeps the accessories block mounted and only toggles `visible`, so
  // an empty list hides it rather than unmounting it.
  const accessories = mapAccessories(financial);

  const { isPreparingModal, prepareError, forceExitWarningOpen, setForceExitWarningOpen, retry } =
    useCallScriptOpenOrchestration({
      open,
      approvalId,
      applicantId,
      readOnly,
      onOpenChange,
      prepareSaveFailedMessage: t("review.customer.callScriptModal.prepareSaveFailed"),
    });

  const [verified, setVerified] = useState<ResidenceVerifiedFields>(EMPTY_RESIDENCE_VERIFIED_FIELDS);
  const [note, setNote] = useState("");
  const [doNotContactPrimary, setDoNotContactPrimary] = useState(false);
  const [dncConfirmOpen, setDncConfirmOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ResidenceFieldErrors>({});
  // Screen-only mirror (the legacy popup has the same checkbox beside the
  // Mailing Address header): while checked, the six mailing rows copy physical
  // and their inputs are disabled.
  const [mailingSameAsPhysical, setMailingSameAsPhysical] = useState(false);
  const [accessoryAnswers, setAccessoryAnswers] = useState<Record<string, string>>({});
  const [step, setStep] = useState<Step>("verification");
  const [isStandardizing, setIsStandardizing] = useState(false);
  // Field to focus once "Edit address" brings the analyst back to the script.
  const [pendingFocusKey, setPendingFocusKey] = useState<ResidenceFieldKey>();
  const [changedFieldRows, setChangedFieldRows] = useState<Array<ChangedFieldRow<ResidenceFieldKey>>>([]);
  // Save first awaits the question catalog (see submitSave); a failure there
  // never reaches the mutation, so it's tracked separately from saveCallScript.error.
  const [isResolvingQuestions, setIsResolvingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState<string>();

  const history = useCallScriptResidenceHistory(approvalId ?? "", applicantId ?? "", { enabled: open && hasIds });
  const questionCatalog = useQuery(
    callScriptResidenceQuestionsQuery(
      approvalId ?? "",
      applicantId ?? "",
      resolveResidenceVerificationQuestionType(personPath),
      open && hasIds
    )
  );
  // Until the catalog loads every question row shows, so the layout doesn't
  // collapse; once it loads, rows whose question isn't active are hidden.
  const activeQuestions = questionCatalog.data ? buildResidenceQuestionPkMap(questionCatalog.data) : undefined;
  // An already-DNC primary phone can't be un-marked, so the checkbox and the
  // Primary Number input are locked.
  const savedPrimaryPhone = rankPhones(selectLivePhones(residence?.phones))[0];
  const dncLocked = savedPrimaryPhone?.isDoNotCall === true;
  // DNC needs a primary number to land on: the saved one, or a complete one
  // entered on this call (CAPS2 assumes phone #1 exists).
  const dncAvailable = savedPrimaryPhone !== undefined || verified.primaryNumber.replaceAll(/\D/gu, "").length === 10;
  const saveCallScript = useSaveCallScriptResidence();
  const addressStandardization = useCallScriptAddressStandardization();
  const addressCompare = addressStandardization.compare;
  useEffect(() => {
    if (open && approvalId && applicantId) {
      prefetchAllCallScriptResidenceQuestions(queryClient, approvalId, applicantId);
    }
  }, [open, approvalId, applicantId, queryClient]);

  const firstName = useWatch({ control, name: `${personPath}.customerInfo.firstName` });
  const lastName = useWatch({ control, name: `${personPath}.customerInfo.lastName` });
  const ssn = useWatch({ control, name: `${personPath}.customerInfo.ssnOrItin` });
  const primaryPhone = useWatch({ control, name: `${personPath}.customerInfo.phones.0.number` });
  const personName = [firstName, lastName].filter(Boolean).join(" ");
  const personLabel = t(personPath === "driver" ? "signers.driver" : "signers.secondSigner");
  const noSavedValue = t("review.customer.callScriptModal.noSavedValue");

  const entries = history.data?.entries ?? [];
  const savedFieldsBaseline = mapResidenceToSavedFields(residence);
  // Phone Number Dialed has no saved counterpart of its own — the last call's
  // dialed number is what "Saved Information" shows, same as the employment
  // modal reads it off the newest history entry.
  const lastCallPhoneNoBad = entries[0]?.isPhoneNoBad === "Y";
  const savedFields: ResidenceVerifiedFields = {
    ...savedFieldsBaseline,
    ...mapFinancialToSavedDealFields(financial),
    phoneNumberDialed: entries[0]?.phoneNumberDialed ?? savedFieldsBaseline.phoneNumberDialed,
    wasOriginalNumberBad: lastCallPhoneNoBad,
  };

  /**
   * Clears "Verified Information" back to blank. Shared by the on-open reset
   * and the post-save reset.
   */
  function resetVerifiedInformation() {
    addressStandardization.reset();
    setVerified({ ...EMPTY_RESIDENCE_VERIFIED_FIELDS, wasOriginalNumberBad: lastCallPhoneNoBad });
    setFieldErrors({});
    setMailingSameAsPhysical(false);
    setAccessoryAnswers({});
  }

  // "Verified Information" is what the analyst captures live on the call, so
  // every open starts from blank.
  useEffect(() => {
    if (open) {
      resetVerifiedInformation();
      setNote("");
      setDoNotContactPrimary(false);
      setDncConfirmOpen(false);
      setStep("verification");
      setPendingFocusKey(undefined);
      setChangedFieldRows([]);
      setQuestionsError(undefined);
    } else {
      // Settle a standardization still waiting on the analyst.
      addressStandardization.onCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetVerifiedInformation is stable per render; only `open` should retrigger this
  }, [open]);

  // "Was original number bad?" starts from the latest call's answer (CAPS2
  // never resets it), which arrives after open and again after each save.
  const latestCallPk = entries[0]?.verificationPhoneCallPk;
  useEffect(() => {
    if (open) {
      setVerified((previous) => ({ ...previous, wasOriginalNumberBad: lastCallPhoneNoBad }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-sync only when the latest call changes
  }, [open, latestCallPk]);

  // TanStack Query v5 dropped per-query `onError`, so query failures are
  // toasted from an effect (mirrors CallScriptModal).
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const toastedHistoryErrorReference = useRef<unknown>(undefined);
  useEffect(() => {
    if (history.isError && history.error !== toastedHistoryErrorReference.current) {
      toastedHistoryErrorReference.current = history.error;
      toast.error(t("review.customer.callScriptModal.history.error", { message: getErrorMessage(history.error) }));
    } else if (!history.isError) {
      toastedHistoryErrorReference.current = undefined;
    }
  }, [history.isError, history.error, t]);

  const rows = buildRows(t);
  const isSaving = saveCallScript.isPending || isResolvingQuestions || isStandardizing;
  const canSave = hasIds && !readOnly && !isSaving && !isPreparingModal && !prepareError;
  const saveErrorMessage =
    questionsError ?? (saveCallScript.isError ? getErrorMessage(saveCallScript.error) : undefined);

  function updateVerified(key: ResidenceFieldKey, value: ResidenceFieldValue) {
    setFieldErrors((previous) => {
      if (!previous[key] && !(key === "primaryNumber" || key === "secondaryNumber")) {
        return previous;
      }
      const next = { ...previous };
      delete next[key];
      // Editing either phone can resolve a duplicate flagged on the other.
      for (const phoneKey of ["primaryNumber", "secondaryNumber"] as const) {
        if (next[phoneKey] === "duplicatePhone") {
          delete next[phoneKey];
        }
      }
      return next;
    });
    setVerified((previous) => {
      const next = { ...previous, [key]: value };
      // Keep mailing in lockstep while the mirror is on — the mailing inputs
      // are disabled in that state, so this is their only writer.
      if (mailingSameAsPhysical) {
        for (const [mailingKey, physicalKey] of MAILING_MIRROR_PAIRS) {
          if (physicalKey === key) {
            next[mailingKey] = value as never;
          }
        }
      }
      return next;
    });
  }

  /**
   * Builds the ZIP-lookup writer for one address block.
   * @param {ResidenceFieldKey} cityKey - The block's City field.
   * @param {ResidenceFieldKey} stateKey - The block's State field.
   * @returns {(result: ZipAutofillResult) => void} The writer.
   */
  function fillCityState(cityKey: ResidenceFieldKey, stateKey: ResidenceFieldKey) {
    return ({ city, state }: ZipAutofillResult) => {
      if (city !== undefined) {
        updateVerified(cityKey, city);
      }
      if (state !== undefined) {
        updateVerified(stateKey, state);
      }
    };
  }

  const physicalZipStatus = useCallScriptZipAutofill(
    verified.physicalZip,
    true,
    fillCityState("physicalCity", "physicalState")
  );
  // While mirroring, mailing City/State follow the physical lookup.
  const mailingZipStatus = useCallScriptZipAutofill(
    verified.mailingZip,
    !mailingSameAsPhysical,
    fillCityState("mailingCity", "mailingState")
  );

  // Reacts live to the analyst's answer; falls back to the saved type until one is picked.
  const effectiveOwnerType = verified.residenceOwnerType || savedFields.residenceOwnerType;
  const landlordVisible = effectiveOwnerType !== "" && effectiveOwnerType !== RESIDENCE_OWNER_TYPE_DEFAULT;
  const landlordZipStatus = useCallScriptZipAutofill(
    verified.landlordZip,
    landlordVisible,
    fillCityState("landlordCity", "landlordState")
  );

  function handleMailingSameAsPhysicalChange(checked: boolean) {
    addressStandardization.reset("mailing");
    setMailingSameAsPhysical(checked);
    setVerified((previous) => {
      const next = { ...previous };
      for (const [mailingKey, physicalKey] of MAILING_MIRROR_PAIRS) {
        // Unchecking clears mailing rather than leaving the mirrored values
        // behind, so the analyst always re-enters from blank (matches the
        // Residence tab's useMirrorAddress).
        next[mailingKey] = (checked ? previous[physicalKey] : "") as never;
      }
      return next;
    });
  }

  /**
   * Whether a row renders: landlord rows only while the residence isn't owned
   * by Self or Spouse; rows without a question otherwise always do; question
   * rows only while their question is active in the deal's catalog.
   * @param {Row} row - The row definition.
   * @returns {boolean} True when the row should be rendered.
   */
  function isRowVisible(row: Row): boolean {
    if (row.landlord && !landlordVisible) {
      return false;
    }
    if (row.type === "section" || row.flag === undefined) {
      return true;
    }
    return activeQuestions === undefined || activeQuestions[row.flag] !== undefined;
  }

  const visibleFieldRows = rows.filter((row): row is FieldRow => row.type === "field" && isRowVisible(row));

  function isFieldVisible(key: ResidenceFieldKey): boolean {
    return visibleFieldRows.some((row) => row.key === key);
  }

  /**
   * Persists the call, then clears "Verified Information" for the next one.
   * The employment modal re-checks force-exit again right here, at the moment
   * Save is confirmed (its cached value could be stale by then); this modal
   * only re-checks once, on open (see useCallScriptOpenOrchestration) — a
   * second check at save-time is deliberately out of scope for now.
   *
   * The question catalog is awaited (not read from cache) so a Save that
   * races the modal-open prefetch still resolves every answer's
   * verificationQuestionPk instead of posting an empty verificationAnswers[]
   * — see ensureCallScriptResidenceQuestionsForPerson.
   * @param {ResidenceVerifiedFields} verifiedValues - The values to save, with any standardized address applied.
   * @param {boolean} physicalAddressComplete - Whether physical fields include intentional blanks.
   */
  async function submitSave(verifiedValues: ResidenceVerifiedFields, physicalAddressComplete: boolean) {
    if (!approvalId || !applicantId) {
      return;
    }
    setQuestionsError(undefined);
    setIsResolvingQuestions(true);
    let questions;
    try {
      questions = await ensureCallScriptResidenceQuestionsForPerson(queryClient, approvalId, applicantId, personPath);
    } catch (error) {
      if (!(error instanceof CancelledError)) {
        setQuestionsError(getErrorMessage(error));
      }
      return;
    } finally {
      setIsResolvingQuestions(false);
    }
    setStep("verification");
    const questionPkMap = buildResidenceQuestionPkMap(questions);
    // Entered values whose questions aren't in this deal's catalog would post
    // an empty verificationAnswers[], which recap rejects.
    if (buildResidenceVerificationAnswers(verifiedValues, savedFields, questionPkMap).length === 0) {
      setQuestionsError(t("review.customer.callScriptModal.noAnswers"));
      return;
    }
    // Accessories have no catalog question; preserve their answers in the DPSC note.
    const accessoryNoteLines = accessories.flatMap(({ id, description }) => {
      const answer = accessoryAnswers[id];
      return answer
        ? [
            `${t("review.customer.callScriptModal.accessories.accessory")} - ${description}; ${t("review.customer.callScriptModal.accessories.purchased")} - ${answer}`,
          ]
        : [];
    });
    saveCallScript.mutate(
      {
        approvalId,
        applicantId,
        personPath,
        note: [...accessoryNoteLines, note.trim()].filter(Boolean).join("\n"),
        noteFields: visibleFieldRows.map(({ key, label }) => ({ key, label })),
        doNotContactPrimary: doNotContactPrimary && dncAvailable && !dncLocked,
        verifiedFields: verifiedValues,
        physicalAddressComplete,
        savedFields,
        savedPhones: residence?.phones,
        questionPkMap,
        previousVerificationOutcome: entries[0]?.verificationOutcome,
      },
      {
        onSuccess: (result) => {
          // `undefined` means the mutationFn no-op'd on a force-exit — nothing
          // was saved, so don't wipe what the analyst just captured.
          if (!result) {
            return;
          }
          setNote("");
          resetVerifiedInformation();
          // CAPS2 reloads the flag from the saved phone: once the dashboard
          // refetches, a DNC that took effect shows through dncLocked.
          setDoNotContactPrimary(false);
        },
      }
    );
  }

  /**
   * Confirmed save: standardizes the address (CAPS2 runs it after the
   * confirm OK, before persisting), then saves. Anything that sends the
   * analyst back to the script keeps every answer.
   */
  async function proceedToSave() {
    setIsStandardizing(true);
    let result;
    try {
      result = await addressStandardization.run({ verified, saved: savedFields, mailingSameAsPhysical });
    } finally {
      setIsStandardizing(false);
    }
    setVerified(result.verified);
    if (!result.proceed) {
      setStep("verification");
      setPendingFocusKey(result.focusKey);
      return;
    }
    await submitSave(result.verified, result.physicalAddressComplete);
  }

  useEffect(() => {
    if (step === "verification" && !addressCompare && pendingFocusKey) {
      // Attribute selector: useId's ":" would need escaping in an #id selector.
      document.querySelector<HTMLElement>(`[id="${uid}-${pendingFocusKey}"]`)?.focus();
      setPendingFocusKey(undefined);
    }
  }, [step, addressCompare, pendingFocusKey, uid]);

  /**
   * Header close / Escape steps back one panel, like dismissing the popup on
   * top did in CAPS2; only from the script itself does it close the modal.
   * @param {boolean} nextOpen - The shell's requested open state.
   */
  function handleShellOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    if (addressCompare) {
      addressStandardization.onCancel();
      return;
    }
    if (isSaving) {
      return;
    }
    if (step === "confirm") {
      setStep("verification");
      return;
    }
    onOpenChange(false);
  }

  /**
   * Save click: shows the "CAPS WARNING" confirmation listing only the changed
   * fields, or goes straight on when nothing actually changed.
   */
  function handleSaveClick() {
    if (!canSave) {
      return;
    }
    const errors = validateResidenceCallScript(verified, savedFields, isFieldVisible);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    const changed = buildChangedFieldRows(visibleFieldRows, verified, savedFields, { noSavedValue });
    if (changed.length === 0) {
      void proceedToSave();
      return;
    }
    setChangedFieldRows(changed);
    setStep("confirm");
  }

  const historyEntryHeader = t("review.customer.callScriptModal.history.entryHeader", {
    personLabel: personLabel.toUpperCase(),
  });

  return (
    <>
      <CallScriptDialogShell
        open={open}
        onOpenChange={handleShellOpenChange}
        title={t("review.customer.callScriptModal.title", { personLabel })}
        description={t("review.customer.callScriptModal.description")}
        closeLabel={t("review.customer.callScriptModal.close")}
        headerTestId="residence-call-script-header"
      >
        {isPreparingModal && (
          <div
            className="gap-(--spacing-3) px-(--spacing-6) py-(--spacing-8) bg-card flex flex-col"
            role="status"
            aria-label={t("review.customer.callScriptModal.preparing")}
          >
            <Skeleton loading active paragraph={{ rows: 6 }} />
          </div>
        )}

        {!isPreparingModal && prepareError && (
          <div className="gap-(--spacing-4) px-(--spacing-6) py-(--spacing-8) bg-card flex flex-col">
            <p
              role="alert"
              className="text-destructive text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)"
            >
              {t("review.customer.callScriptModal.prepareErrorPrefix", { message: prepareError })}
            </p>
            <div className="gap-(--spacing-2) flex">
              <Button type="button" size="xs" onClick={retry}>
                {t("review.customer.callScriptModal.retry")}
              </Button>
              <Button type="button" variant="outline" size="xs" onClick={() => onOpenChange(false)}>
                {t("review.customer.callScriptModal.exit")}
              </Button>
            </div>
          </div>
        )}

        {!isPreparingModal && !prepareError && addressCompare && (
          <section aria-labelledby={`${uid}-address-compare-title`} className="flex flex-col">
            <h2
              id={`${uid}-address-compare-title`}
              className="text-primary pt-(--spacing-3) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) px-5"
            >
              {t("review.customer.residence.addressVerification.title", {
                personLabel,
                slotLabel: t(
                  addressCompare.kind === "physical"
                    ? "review.customer.residence.physicalAddress"
                    : "review.customer.residence.mailingAddress"
                ),
              })}
            </h2>
            <AddressVerificationPanel
              key={`${addressCompare.kind}-${addressCompare.status}-${Object.values(addressCompare.entered).join("|")}`}
              status={addressCompare.status}
              entered={addressCompare.entered}
              suggested={addressCompare.suggested}
              onSelect={addressStandardization.onSelect}
              onIgnore={addressStandardization.onIgnore}
              onEdit={addressStandardization.onEdit}
              onCancel={addressStandardization.onCancel}
            />
          </section>
        )}

        {!isPreparingModal && !prepareError && !addressCompare && step === "confirm" && (
          <section aria-labelledby={`${uid}-confirm-save-title`} className="px-(--spacing-6) py-(--spacing-4) bg-card">
            <CallScriptConfirmSaveContent
              title={
                <h2
                  id={`${uid}-confirm-save-title`}
                  className="text-(length:--typography-base-sizes-large-font-size)/(--typography-base-sizes-large-line-height) font-(--font-weight-semibold)"
                >
                  {t("review.customer.callScriptModal.confirmSave.title")}
                </h2>
              }
              rows={changedFieldRows}
              onConfirm={() => void proceedToSave()}
              onCancel={() => setStep("verification")}
              disabled={isSaving}
              error={questionsError}
              labels={{
                title: t("review.customer.callScriptModal.confirmSave.title"),
                message: t("review.customer.callScriptModal.confirmSave.message"),
                question: t("review.customer.callScriptModal.confirmSave.question"),
                verifiedInformation: t("review.customer.callScriptModal.verifiedInformation"),
                savedInformation: t("review.customer.callScriptModal.savedInformation"),
                confirm: t("review.customer.callScriptModal.confirmSave.ok"),
                cancel: t("review.customer.callScriptModal.confirmSave.cancel"),
              }}
            />
          </section>
        )}

        {/* Hidden, not unmounted, behind the other panels so answers and
          scroll position survive the confirm / address-compare steps. */}
        {!isPreparingModal && !prepareError && (
          <div className="flex flex-col" hidden={step !== "verification" || addressCompare !== undefined}>
            <div className="px-(--spacing-4) bg-card">
              <CallScriptSaveBanners
                error={
                  saveErrorMessage
                    ? t("review.customer.callScriptModal.saveError", { message: saveErrorMessage })
                    : undefined
                }
              />
              <div className="gap-(--spacing-2) py-(--spacing-2) grid h-[75vh] grid-cols-1 lg:grid-cols-[2fr_1fr]">
                {/* Verified vs Saved comparison */}
                <div className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) grid min-h-0 grid-cols-1 overflow-y-auto sm:grid-cols-[auto_1fr_1fr]">
                  <div />
                  <div className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
                    {t("review.customer.callScriptModal.verifiedInformation")}
                  </div>
                  <div className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
                    {t("review.customer.callScriptModal.savedInformation")}
                  </div>

                  {rows.map((row) => {
                    if (!isRowVisible(row)) {
                      return;
                    }
                    if (row.type === "section") {
                      return (
                        <div key={row.key} className="contents">
                          <span className="text-primary pt-(--spacing-2) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) self-center pr-1.5 text-right">
                            {row.label}
                          </span>
                          {row.withSameAsPhysical ? (
                            <div className="pt-(--spacing-2) flex items-center gap-1.5 self-center">
                              <Checkbox
                                id={`${uid}-mailing-same-as-physical`}
                                checked={mailingSameAsPhysical}
                                onCheckedChange={(checked) => handleMailingSameAsPhysicalChange(checked === true)}
                                disabled={readOnly}
                              />
                              <label
                                htmlFor={`${uid}-mailing-same-as-physical`}
                                className="text-muted-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)"
                              >
                                {t("review.customer.callScriptModal.mailingSameAsPhysical")}
                              </label>
                            </div>
                          ) : (
                            <div />
                          )}
                          <div />
                        </div>
                      );
                    }
                    const id = `${uid}-${row.key}`;
                    const mirrored = mailingSameAsPhysical && MAILING_FIELD_KEYS.has(row.key);
                    const fieldError = fieldErrors[row.key];
                    return (
                      <div key={row.key} className="contents [&>*]:py-0">
                        {/* Not a <label htmlFor>: clicking it must not focus/activate the
                          Verified input — CallScriptFieldControl gets its name via aria-label. */}
                        <span className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-medium) self-center pr-1.5 text-right">
                          {row.label}
                        </span>
                        <div
                          className={cn(
                            "max-w-[90%] self-center",
                            isRowChanged(row, verified[row.key], savedFields[row.key]) && "rounded-md bg-amber-100"
                          )}
                          data-testid={`${row.key}-verified-cell`}
                        >
                          <CallScriptFieldControl
                            row={row}
                            id={id}
                            value={verified[row.key]}
                            onChange={(value) => updateVerified(row.key, value)}
                            onBlur={
                              UPPERCASE_ON_BLUR_KEYS.has(row.key)
                                ? () => {
                                    const value = verified[row.key];
                                    if (typeof value === "string" && value !== value.toUpperCase()) {
                                      updateVerified(row.key, value.toUpperCase());
                                    }
                                  }
                                : undefined
                            }
                            disabled={readOnly || mirrored || (row.key === "primaryNumber" && dncLocked)}
                            ariaLabel={row.label}
                            invalid={fieldError !== undefined}
                          />
                          {row.key === "physicalZip" && <ZipLookupMessage status={physicalZipStatus} />}
                          {row.key === "mailingZip" && <ZipLookupMessage status={mailingZipStatus} />}
                          {row.key === "landlordZip" && <ZipLookupMessage status={landlordZipStatus} />}
                          {fieldError && (
                            <p
                              role="alert"
                              className="text-destructive mt-(--spacing-1) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)"
                            >
                              {t(`review.customer.callScriptModal.errors.${fieldError}`, { personLabel })}
                            </p>
                          )}
                        </div>
                        <div className="max-w-[90%] self-center">
                          <CallScriptFieldControl
                            row={row}
                            id={`${id}-saved`}
                            value={savedFields[row.key]}
                            onChange={() => {}}
                            disabled
                            ariaLabel={t("review.customer.callScriptModal.savedFieldLabel", { field: row.label })}
                          />
                        </div>
                      </div>
                    );
                  })}

                  {/* Vehicle accessories — CAPS2 toggles `visible`, not `rendered`,
                    so the block stays mounted and is only hidden when the deal
                    has no addons. */}
                  <div className="mt-(--spacing-3) sm:col-span-3" hidden={accessories.length === 0}>
                    <h3 className="text-primary mb-(--spacing-1) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
                      {t("review.customer.callScriptModal.accessories.question")}
                    </h3>
                    <div className="gap-x-(--spacing-3) gap-y-(--spacing-1) grid grid-cols-[1fr_auto_auto]">
                      <span className="text-muted-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) uppercase">
                        {t("review.customer.callScriptModal.accessories.accessory")}
                      </span>
                      <span className="text-muted-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) uppercase">
                        {t("review.customer.callScriptModal.accessories.price")}
                      </span>
                      <span className="text-muted-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) uppercase">
                        {t("review.customer.callScriptModal.accessories.purchased")}
                      </span>
                      {accessories.map((accessory) => (
                        <div key={accessory.id} className="contents">
                          <span className="self-center">{accessory.description}</span>
                          <span className="self-center text-right tabular-nums">
                            {accessory.price === undefined
                              ? noSavedValue
                              : `$${accessory.price.toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}
                          </span>
                          <Select
                            value={accessoryAnswers[accessory.id] ?? ""}
                            onValueChange={(next) =>
                              setAccessoryAnswers((previous) => ({ ...previous, [accessory.id]: next }))
                            }
                            disabled={readOnly}
                          >
                            <SelectTrigger
                              aria-label={t("review.customer.callScriptModal.accessories.purchased")}
                              className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-5 w-16"
                            >
                              <SelectValue placeholder="" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Y">Y</SelectItem>
                              <SelectItem value="N">N</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Side panel: Approval / person / do-not-contact / Notes / History */}
                <div className="flex min-h-0 flex-col gap-2">
                  <div className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) leading-tight">
                    <p>
                      <span className="text-primary font-(--font-weight-semibold)">
                        {t("review.customer.callScriptModal.approvalNumber")}:
                      </span>{" "}
                      {approvalId || noSavedValue}
                    </p>
                    <p>
                      <span className="text-primary font-(--font-weight-semibold)">{personLabel}:</span>{" "}
                      {personName || noSavedValue}
                      {ssn && ` (${formatSsn(ssn)})`}
                    </p>
                    <p>
                      <span className="text-primary font-(--font-weight-semibold)">
                        {t("review.customer.callScriptModal.phoneNumber")}:
                      </span>{" "}
                      {primaryPhone ? formatPhoneOrDigits(String(primaryPhone)) : noSavedValue}
                    </p>
                  </div>

                  <div className="flex items-start gap-1.5">
                    <Checkbox
                      id={`${uid}-do-not-contact-primary`}
                      checked={dncLocked || (doNotContactPrimary && dncAvailable)}
                      onCheckedChange={(checked) => {
                        if (checked === true) {
                          setDncConfirmOpen(true);
                        } else {
                          setDoNotContactPrimary(false);
                        }
                      }}
                      disabled={readOnly || dncLocked || !dncAvailable}
                      className="mt-0.5"
                    />
                    <label
                      htmlFor={`${uid}-do-not-contact-primary`}
                      className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) leading-tight"
                    >
                      {t("review.customer.callScriptModal.doNotContactPrimary", { personLabel })}
                    </label>
                  </div>

                  <div>
                    <label
                      htmlFor={`${uid}-residence-note`}
                      className="text-primary mb-(--spacing-1) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) block"
                    >
                      {t("review.customer.callScriptModal.notes.label")}
                    </label>
                    <Textarea
                      id={`${uid}-residence-note`}
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder={t("review.customer.callScriptModal.notes.placeholder")}
                      maxLength={NOTE_MAX_LENGTH}
                      disabled={readOnly}
                      className="min-h-16 resize-none"
                    />
                  </div>

                  <CallScriptHistoryPanel
                    entries={entries}
                    isLoading={history.isLoading}
                    title={t("review.customer.callScriptModal.history.title")}
                    loadingLabel={t("review.customer.callScriptModal.history.loading")}
                    emptyLabel={t("review.customer.callScriptModal.history.empty")}
                    entryHeader={historyEntryHeader}
                    renderAnswerValue={(answer) => answer.answerEntered}
                  />
                </div>
              </div>

              <div className="text-muted-foreground space-y-(--spacing-2) pb-(--spacing-3) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)">
                <p>{t("review.customer.callScriptModal.footerNotes.accountNumber")}</p>
                <div>
                  <p>{t("review.customer.callScriptModal.footerNotes.downPaymentIntro")}</p>
                  <ul className="list-inside list-disc">
                    <li>{t("review.customer.callScriptModal.footerNotes.downPaymentKnown")}</li>
                    <li>{t("review.customer.callScriptModal.footerNotes.downPaymentUnsure")}</li>
                    <li>{t("review.customer.callScriptModal.footerNotes.downPaymentNone")}</li>
                  </ul>
                </div>
              </div>
            </div>

            <DialogFooter className="bg-muted px-(--spacing-4) py-(--spacing-2) border-t sm:flex-row">
              <Button type="button" size="xs" disabled={!canSave} onClick={handleSaveClick}>
                {t("review.customer.callScriptModal.save")}
              </Button>
              <Button type="button" variant="outline" size="xs" disabled={isSaving} onClick={() => onOpenChange(false)}>
                {t("review.customer.callScriptModal.exit")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </CallScriptDialogShell>

      {/* The box only checks once the analyst accepts this warning; nothing
        is sent until Save. */}
      <ConfirmDialog
        open={dncConfirmOpen}
        onOpenChange={setDncConfirmOpen}
        variant="warning"
        title={t("review.customer.callScriptModal.dncConfirm.title")}
        description={t("review.customer.callScriptModal.dncConfirm.message", { personLabel })}
        cancelAction={{ label: t("review.customer.callScriptModal.dncConfirm.cancel") }}
        confirmAction={{
          label: t("review.customer.callScriptModal.dncConfirm.ok"),
          onClick: () => setDoNotContactPrimary(true),
        }}
      />

      <ForceExitWarningModal
        open={forceExitWarningOpen}
        onOpenChange={setForceExitWarningOpen}
        onConfirm={() => setForceExitWarningOpen(false)}
      />
    </>
  );
}
